import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500 text-lg">未找到该作品</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 text-white rounded-full font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    id: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(
      2026,
      7,
      Math.max(1, 4 - Math.floor(i / 3))
    ).toLocaleDateString('zh-CN'),
  }));

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="w-full bg-background-50 py-6 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1 text-sm text-foreground-500 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link
                to={`/category/${comic.category}`}
                className="hover:text-primary-500 transition-colors cursor-pointer"
              >
                {category?.name || comic.category}漫画
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium truncate max-w-[200px]">
                {comic.title}
              </span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 w-[200px] md:w-[240px] mx-auto md:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                    title={`${comic.title} - 韩国漫画详情`}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">
                  作者：<strong className="text-foreground-700">{comic.author}</strong>
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium">
                    <i className="ri-star-fill"></i>
                    {comic.rating} 分
                  </span>
                  <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-sm">
                    {comic.status === 'completed' ? '已完结' : '连载中'}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-sm">
                    共 {comic.chapters} 话
                  </span>
                  {comic.isHot && (
                    <span className="px-3 py-1 rounded-full bg-accent-500 text-white text-sm font-medium">
                      热门作品
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">
                    作品简介
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <Link
                  to={`/category/${comic.category}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-book-open-line"></i>
                  开始阅读
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-8 md:py-12 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
              章节列表
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {chapters.map((ch) => (
                <div
                  key={ch.id}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <span className="text-sm text-foreground-700 font-medium truncate">
                    {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">
                    {ch.date}
                  </span>
                </div>
              ))}
            </div>
            {comic.chapters > 20 && (
              <p className="text-center text-sm text-foreground-400 mt-4">
                仅显示最近20话，更多章节请开始阅读查看
              </p>
            )}
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="w-full py-8 md:py-12 bg-background-100">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/article/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-50 relative mb-2">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        title={`${rc.title} - 同类推荐韩国漫画`}
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: `${comic.title} - 韩国漫画在线观看`,
              description: comic.description,
              author: {
                '@type': 'Person',
                name: comic.author,
              },
              datePublished: '2026-01-01',
              dateModified: comic.updateDate,
              image: comic.coverImage,
              url: `https://manhwa-reader.example.com/article/${comic.id}`,
              isPartOf: {
                '@type': 'WebSite',
                name: '韩漫阅读',
                url: 'https://manhwa-reader.example.com',
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://manhwa-reader.example.com/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: `${category?.name || comic.category}漫画`,
                    item: `https://manhwa-reader.example.com/category/${comic.category}`,
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: comic.title,
                    item: `https://manhwa-reader.example.com/article/${comic.id}`,
                  },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}